import { NextPage } from 'next'
import Head from 'next/head'
import { useState, useEffect } from 'react'
import { useRouter } from 'next/router'
import { useTranslation } from 'react-i18next'
import Header from '~/components/Header'
import Footer from '~/components/Footer'
import {
  StyledStakingPage,
  StyledContent,
  StyledStakingBox,
} from '../../styled'
import FormHeader from '../components/FormHeader'
import Tabs from '../components/Tabs'
import WrapTabs from './WrapTabs'
import WrapForm from './WrapForm'
import UnWrapForm from './UnWrapForm'
import BrandWatermark from '../components/BrandWatermark'
import FaqSection from '../components/FaqSection'
import { useModalToggle } from '~/state/application/hooks'
import { ApplicationModal } from '~/state/application/reducer'
import { useIsDisableTrade } from '~/state/dex/hooks'
import { useLocalStorage } from '~/hooks/useLocalStorage'
import { lidoServiceWarningId } from '~/components/Modals'
import { useGA } from '~/hooks'
import { getFromParam } from '../utils'

const faqItems = [
  {
    id: 'lido-wrap-faq-1',
    question: 'lido_wrap_faq.question_1',
    answer: 'lido_wrap_faq.answer_1',
  },
  {
    id: 'lido-wrap-faq-2',
    question: 'lido_wrap_faq.question_2',
    answer: 'lido_wrap_faq.answer_2',
  },
  {
    id: 'lido-wrap-faq-3',
    question: 'lido_wrap_faq.question_3',
    answer: 'lido_wrap_faq.answer_3',
  },
]

type WrapTabType = 'wrap' | 'unwrap'

const LidoWrap: NextPage = () => {
  const { t } = useTranslation()
  const { trackView, trackClick } = useGA()
  const router = useRouter()
  const { tab } = router.query

  // Initialize tab from URL query parameter or default to 'wrap'
  const getInitialTab = (): WrapTabType => {
    if (tab === 'unwrap') return 'unwrap'
    return 'wrap'
  }

  const [activeTab, setActiveTab] = useState<WrapTabType>(getInitialTab())

  const isDisableTrade = useIsDisableTrade()
  const toggleIpBlockedModal = useModalToggle(ApplicationModal.IP_BLOCKED)

  const toggleThirdPartyServiceWarningModal = useModalToggle(
    ApplicationModal.THIRD_PARTY_SERVICE_WARNING,
  )
  const [warningVisited, setWarningVisited] = useLocalStorage(
    lidoServiceWarningId,
    false,
  )

  // Update activeTab when URL query changes
  useEffect(() => {
    const newTab = getInitialTab()
    if (newTab !== activeTab) {
      setActiveTab(newTab)
    }
  }, [tab])

  const handleTabChange = (tab: WrapTabType) => {
    const trackId =
      tab === 'wrap' ? 'webtl_stk_wrap_select' : 'webtl_stk_unwrap'
    trackClick(trackId)

    setActiveTab(tab)
    // Update URL when tab changes
    router.push(
      {
        pathname: router.pathname,
        query: { ...router.query, tab },
      },
      undefined,
      { shallow: true },
    )
  }

  useEffect(() => {
    if (isDisableTrade) {
      toggleIpBlockedModal()
    }
  }, [isDisableTrade])

  useEffect(() => {
    trackView('webtl_stk_detail', {
      type: 'wrap',
      from: getFromParam(router.query),
    })

    if (!warningVisited && !isDisableTrade) {
      toggleThirdPartyServiceWarningModal()
      setWarningVisited(true)
    }
  }, [])

  return (
    <StyledStakingPage>
      <Head>
        <title>{t('meta_staking_title')}</title>
        <meta name="description" content={t('meta_staking_description')} />
        <meta name="keywords" content={t('meta_staking_keywords')} />
      </Head>
      <Header />
      <StyledContent>
        <StyledStakingBox root>
          <FormHeader />
          <Tabs currentTab="wrap" />
          <WrapTabs activeTab={activeTab} onChange={handleTabChange} />
          {activeTab === 'wrap' ? <WrapForm /> : <UnWrapForm />}
          <BrandWatermark />
        </StyledStakingBox>
        <FaqSection faqItems={faqItems} from="wrap" />
      </StyledContent>
      <Footer />
    </StyledStakingPage>
  )
}

export default LidoWrap
